import React, { useMemo, useState } from 'react';
import type { Task } from '../types/task.type';

import TaskEditorModal from './TaskEditorModal';
import { formatPriority } from 'src/lib/taskPriority';
import { numCmp, dateCmp, strCmp } from 'src/utils/sort';
import PriorityHelpModal from './PriorityHelpModal';
import { fmtLocaleDateCompact, toProjectDateOnly } from 'src/utils/date';
import TaskOverview from './TaskOverview';
import { useProjectSettingCtx } from 'src/features/projects/contexts/ProjectSettingContext';

import type { CreateTaskInput, UpdateTaskPatch } from '../api/tasks.api';

type SortKey = 'id' | 'createdAt' | 'updatedAt' | 'status' | 'dueDate' | 'priority' | 'title';
type SortDir = 'asc' | 'desc';

export default function TaskListForm({
    projectId,
    tasks,
    loading,
    canEdit = false,
    currentUserId,
    userNameMap,
    onCreate,
    onUpdate,
    onDelete,
    onReload,
}: {
    projectId: string;
    tasks: Task[];
    loading?: boolean;
    canEdit?: boolean;
    currentUserId?: string;
    userNameMap?: Record<string, string>;
    onCreate: (input: CreateTaskInput) => Promise<any>;
    onUpdate: (taskId: string, patch: UpdateTaskPatch) => Promise<any>;
    onDelete: (taskId: string) => Promise<any>;
    onReload: () => void;
}) {
    const { setting } = useProjectSettingCtx();
    const zone = setting?.timeZone ?? 'Asia/Seoul';

    const [q, setQ] = useState('');
    const [showInactive, setShowInactive] = useState(false);
    const [showDone, setShowDone] = useState(true);
    const [view, setView] = useState<'ALL' | 'ASSIGNED_ME' | 'CREATED_ME'>('ALL');

    const [sortKey, setSortKey] = useState<SortKey>('createdAt');
    const [sortDir, setSortDir] = useState<SortDir>('desc');

    const [detailOpen, setDetailOpen] = useState(false);
    const [editing, setEditing] = useState<Task | null>(null);

    const filtered = useMemo(() => {
        let arr = [...tasks];

        if (!showDone) arr = arr.filter((t) => t.status !== 'DONE' && !t.closedAt);
        if (!showInactive) arr = arr.filter((t) => t.isActive);

        if (view === 'ASSIGNED_ME' && currentUserId) arr = arr.filter((t) => t.assigneeUserId === currentUserId);
        else if (view === 'CREATED_ME' && currentUserId) arr = arr.filter((t) => t.createUserId === currentUserId);

        const s = q.trim().toLowerCase();
        if (s) {
            arr = arr.filter((t) => {
                const hay = [
                    t.title,
                    t.description,
                    t.progressNote,
                    String(t.assigneeUserId ?? ''),
                    String(t.createUserId ?? ''),
                ]
                    .filter(Boolean)
                    .join(' ')
                    .toLowerCase();
                return hay.includes(s);
            });
        }
        return arr;
    }, [tasks, q, showInactive, view, currentUserId, showDone]);

    const sorted = useMemo(() => {
        const arr = [...filtered];
        arr.sort((a, b) => {
            let cmp = 0;
            switch (sortKey) {
                case 'createdAt':
                    cmp = dateCmp(a.createdAt, b.createdAt);
                    break;
                case 'updatedAt':
                    cmp = dateCmp(a.updatedAt ?? a.createdAt, b.updatedAt ?? b.createdAt);
                    break;
                case 'status': {
                    const rank = (s: Task['status']) =>
                        s === 'BLOCKED' ? 3 : s === 'IN_PROGRESS' ? 2 : s === 'NOT_STARTED' ? 1 : 0;
                    cmp = numCmp(rank(a.status), rank(b.status));
                    break;
                }
                case 'dueDate':
                    cmp = dateCmp(a.dueDate, b.dueDate);
                    break;
                case 'priority':
                    cmp = numCmp(a.priorityScore ?? null, b.priorityScore ?? null);
                    break;
                case 'title':
                    cmp = strCmp(a.title, b.title);
                    break;
                default:
                    cmp = 0;
            }
            return sortDir === 'asc' ? cmp : -cmp;
        });
        return arr;
    }, [filtered, sortKey, sortDir]);

    const openNew = () => {
        setEditing(null);
        setDetailOpen(true);
    };
    const openEdit = (t: Task) => {
        setEditing(t);
        setDetailOpen(true);
    };

    const renderUserCell = (userId?: string | null) => {
        if (!userId) return <span className="text-gray-400">—</span>;

        const label = userNameMap?.[userId];

        // label 포맷: "Name <email>" (TaskListContainer에서 이렇게 만듦)
        let name: string | null = null;
        let email: string | null = null;

        if (typeof label === 'string') {
            const m = label.match(/^(.*)\s<([^>]+)>$/);
            if (m) {
                name = m[1].trim() || null;
                email = m[2].trim() || null;
            } else if (label.includes('@')) {
                // 이메일만 있거나, 이름만 있는 경우
                email = label.trim() || null;
            } else {
                name = label.trim() || null;
            }
        }

        // fallback
        if (!name && !email) {
            return <span className="text-gray-700">{userId.slice(0, 8)}</span>;
        }

        return (
            <div className="leading-tight">
                {name ? <div className="text-sm font-medium text-gray-900">{name}</div> : null}
                {email ? <div className="text-xs text-gray-500">{email}</div> : null}
            </div>
        );
    };

    return (
        <div className="w-full space-y-4">
            {/* 전체기준 Overview */}
            <TaskOverview tasks={tasks} userNameMap={userNameMap} />

            {/* 상단: 필터/액션 */}
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                    <input
                        className="w-64 rounded border px-2 py-1 text-sm"
                        placeholder="Search tasks…"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                    />
                    {/* 보기 필터 */}
                    <div className="inline-flex rounded-lg border overflow-hidden text-xs">
                        <button
                            className={`px-2.5 py-1 ${view === 'ALL' ? 'bg-gray-900 text-white' : 'hover:bg-gray-50'}`}
                            onClick={() => setView('ALL')}
                        >
                            All
                        </button>
                        <button
                            className={`px-2.5 py-1 border-l ${
                                view === 'ASSIGNED_ME' ? 'bg-gray-900 text-white' : 'hover:bg-gray-50'
                            }`}
                            onClick={() => setView('ASSIGNED_ME')}
                            disabled={currentUserId == null}
                            title="Assigned to me"
                        >
                            Mine
                        </button>
                        <button
                            className={`px-2.5 py-1 border-l ${
                                view === 'CREATED_ME' ? 'bg-gray-900 text-white' : 'hover:bg-gray-50'
                            }`}
                            onClick={() => setView('CREATED_ME')}
                            disabled={currentUserId == null}
                            title="Created by me"
                        >
                            Assigned by me
                        </button>
                    </div>
                    {/* 활성 표시 */}
                    {/* Show Inactive 숨김 */}
                    {/* <label className="inline-flex items-center gap-2 text-xs ml-2">
                                <input
                                    type="checkbox"
                                    className="accent-gray-700"
                                    checked={showInactive}
                                    onChange={(e) => setShowInactive(e.target.checked)}
                                />
                                <span>Show inactive</span>
                            </label> */}
                    <label className="inline-flex items-center gap-2 text-xs ml-2">
                        <input
                            type="checkbox"
                            className="accent-gray-700"
                            checked={showDone}
                            onChange={(e) => setShowDone(e.target.checked)}
                        />
                        <span>Show Done</span>
                    </label>
                </div>
                {/* 선택: 색상 범례 */}
                <div className="flex items-center gap-2 text-[11px] text-gray-600">
                    <span className="inline-flex items-center gap-1">
                        <i className="w-3 h-3 rounded bg-emerald-200" /> Low (≤8)
                    </span>
                    <span className="inline-flex items-center gap-1">
                        <i className="w-3 h-3 rounded bg-amber-200" /> Medium (9–15)
                    </span>
                    <span className="inline-flex items-center gap-1">
                        <i className="w-3 h-3 rounded bg-rose-200" /> High (≥16)
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="text-xs text-gray-500">정렬:</div>
                    <select
                        className="text-xs rounded border px-2 py-1"
                        value={sortKey}
                        onChange={(e) => setSortKey(e.target.value as SortKey)}
                    >
                        <option value="id">ID</option>
                        <option value="createdAt">Created</option>
                        <option value="updatedAt">Updated</option>
                        <option value="status">Status</option>
                        <option value="dueDate">Due</option>
                        <option value="priority">Priority</option>
                        <option value="title">Title</option>
                    </select>
                    <button
                        className="text-xs rounded border px-2 py-1 hover:bg-gray-50"
                        onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
                        title="정렬 방향 전환"
                    >
                        {sortDir === 'asc' ? '▲ asc' : '▼ desc'}
                    </button>

                    <button
                        className="text-xs rounded-lg border px-2.5 py-1.5 bg-white hover:bg-gray-50 disabled:opacity-50"
                        onClick={openNew}
                        disabled={!canEdit}
                    >
                        + New Task
                    </button>
                </div>
            </div>

            {/* 리스트 */}
            <div className="rounded-2xl border bg-white overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-[1200px] w-full text-sm">
                        <thead>
                            <tr className="text-left text-gray-500">
                                {/* <th className="px-3 py-3 //w-14">ID</th> */}
                                <th className="px-3 py-3 //w-14">SEQ.</th>
                                <th className="px-3 py-3 //w-[320px] //w-[10%] min-w-[120px]">
                                    <div>Title</div>
                                    <div className="text-xs text-gray-500 font-medium">제목</div>
                                </th>
                                <th className="px-3 py-3 //w-[320px] //w-[15%] min-w-[240px]">
                                    <div>Description</div>
                                    <div className="text-xs text-gray-500 font-medium">상세 설명</div>
                                </th>
                                <th className="px-3 py-3 //w-[320px] //w-[25%] min-w-[240px]">
                                    <div>Progress Note</div>
                                    <div className="text-xs text-gray-500 font-medium">진행 상황 메모</div>
                                </th>
                                <th className="px-3 py-3 //w-28">
                                    <div>
                                        <span className="inline-flex items-center">
                                            Priority
                                            <PriorityHelpModal />
                                        </span>
                                    </div>
                                    <div className="text-xs text-gray-500 font-medium">우선순위</div>
                                </th>
                                <th className="px-3 py-3 //w-24 min-w-[90px]">
                                    <div>U / I</div>
                                    <div className="text-xs text-gray-500 font-medium">긴급도/중요도</div>
                                </th>
                                <th className="px-3 py-3 //w-28">
                                    <div>Due Date</div>
                                    <div className="text-xs text-gray-500 font-medium">완료 목표일</div>
                                </th>
                                <th className="px-3 py-3 //w-28">
                                    <div>Assignee</div>
                                    <div className="text-xs text-gray-500 font-medium">담당자</div>
                                </th>
                                <th className="px-3 py-3 //w-28">
                                    <div>Created By</div>
                                    <div className="text-xs text-gray-500 font-medium">생성자</div>
                                </th>
                                <th className="px-3 py-3 //w-32 min-w-[110px]">
                                    <div>Updated At</div>
                                    <div className="text-xs text-gray-500 font-medium">내용 갱신일</div>
                                </th>
                                <th className="px-3 py-3 //w-28">
                                    <div>Status</div>
                                    <div className="text-xs text-gray-500 font-medium">진행 상황</div>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr>
                                    <td colSpan={9} className="px-3 py-6 text-center text-gray-400">
                                        Loading…
                                    </td>
                                </tr>
                            )}
                            {!loading &&
                                sorted.map((t, idx) => (
                                    <tr
                                        key={t.id}
                                        className="border-t hover:bg-gray-50 cursor-pointer"
                                        onClick={() => openEdit(t)}
                                        title="클릭하여 상세 보기/수정"
                                    >
                                        {/* <td className="px-3 py-2 font-mono text-xs">{t.id}</td> */}
                                        <td className="px-3 py-2 font-mono text-xs">{idx + 1}</td>
                                        {/* Title */}
                                        <td className="px-3 py-2">
                                            <div className="font-medium line-clamp-2 whitespace-pre-wrap">
                                                {t.title}
                                            </div>
                                        </td>
                                        {/* Description */}
                                        <td className="px-3 py-2">
                                            <div className="line-clamp-2 whitespace-pre-wrap">
                                                {t.description || '-'}
                                            </div>
                                        </td>
                                        {/* Progress Note */}
                                        <td className="px-3 py-2">
                                            <div className="line-clamp-5 whitespace-pre-wrap">
                                                {t.progressNote || '-'}
                                            </div>
                                        </td>

                                        {/* Priority 컬럼 */}
                                        <td className="px-3 py-2">
                                            {(() => {
                                                const { text, className } = formatPriority({
                                                    scoreStored: t.priorityScore,
                                                    quadStored: t.priorityQuadrant,
                                                    urgency: t.urgency, // 저장값 없을 때만 계산에 활용
                                                    importance: t.importance,
                                                });
                                                return (
                                                    <span
                                                        className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs ${className}`}
                                                    >
                                                        {text} {/* 예: "10 DELEGATE" */}
                                                    </span>
                                                );
                                            })()}
                                        </td>
                                        {/* 긴급도/중요도 컬럼 */}
                                        <td className="px-3 py-2">
                                            <span className="text-xs">
                                                {t.urgency} / {t.importance}
                                            </span>
                                        </td>
                                        {/* 완료 목표일 컬럼 */}
                                        <td className="px-3 py-2">{t.dueDate || '-'}</td>
                                        {/* 담당자 컬럼 */}
                                        {/* <td className="px-3 py-2">
                                            {t.assigneeUserId != null
                                                ? userNameMap?.[t.assigneeUserId] ?? t.assigneeUserId
                                                : '—'}
                                        </td> */}
                                        <td className="px-3 py-2">{renderUserCell(t.assigneeUserId)}</td>
                                        {/* 생성자 컬럼 */}
                                        {/* <td className="px-3 py-2">
                                            {t.createUserId != null
                                                ? userNameMap?.[t.createUserId] ?? t.createUserId
                                                : '—'}
                                        </td> */}
                                        <td className="px-3 py-2">{renderUserCell(t.createUserId)}</td>
                                        {/* 내용 갱신일 컬럼 */}
                                        <td className="px-3 py-2">{toProjectDateOnly(t.updatedAt, zone) || '-'}</td>
                                        {/* Status / 진행 상황 컬럼 */}
                                        <td className="px-3 py-2">
                                            <div>{t.status}</div>
                                            <div className="text-xs text-gray-500">
                                                {t.closedAt ? fmtLocaleDateCompact(t.closedAt) : ''}
                                            </div>
                                        </td>
                                    </tr>
                                ))}

                            {sorted.length === 0 && (
                                <tr>
                                    <td colSpan={10} className="px-3 py-6 text-center text-gray-500">
                                        등록된 태스크가 없습니다.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <TaskEditorModal
                open={detailOpen}
                onClose={() => setDetailOpen(false)}
                projectId={projectId}
                task={editing}
                canEdit={canEdit}
                currentUserId={currentUserId}
                userNameMap={userNameMap}
                onCreate={onCreate}
                onUpdate={onUpdate}
                onDelete={onDelete}
                onSaved={() => onReload()}
                onDeleted={() => onReload()}
            />
        </div>
    );
}
